import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Loader2,
  BookOpen,
  Trophy,
  Calendar,
  MessageSquare,
  AlertCircle,
  FileText,
  CheckCircle,
  Clock,
  Flame,
  Zap,
  GraduationCap,
  ArrowRight,
  Mail,
  Sparkles,
  Star,
  TrendingUp,
} from "lucide-react";
import { RoleGuard } from "@/components/auth/RoleGuard";
import { motion } from "motion/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export const Route = createFileRoute("/_authenticated/parent")({
  head: () => ({ meta: [{ title: "Parent Portal — Code Cubs" }] }),
  component: ParentPortal,
});

/* ───────────────── Child interface (preserved exactly) ───────────────── */

interface Child {
  student_id: string;
  profile: { full_name: string | null; points: number; level: number } | null;
  lessons_done: number;
  lessons_total: number;
  quizzes_taken: number;
  avg_score: number;
  attendance_present: number;
  attendance_total: number;
  badges: number;
  notes: { content: string; created_at: string }[];
  assignments_pending: number;
  assignments_submitted: number;
  assignments_avg_grade: number;
  absence_alerts: { session_date: string; status: string }[];
  recent_quizzes: { percentage: number; created_at: string }[];
}

/* ───────────────── Chart tooltip style (CSS-var aware) ───────────────── */

const chartTooltipStyle: React.CSSProperties = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0.75rem",
  color: "hsl(var(--card-foreground))",
  padding: "8px 12px",
  fontSize: "0.8rem",
  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
};

/* ───────────────── Pie chart palette ───────────────── */

const PIE_COLORS = ["#f97316", "#3b82f6", "#10b981", "#a855f7"];

/* ───────────────── Animation helpers ───────────────── */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: "easeOut" as const },
};

const stagger = (i: number) => ({
  ...fadeUp,
  transition: { ...fadeUp.transition, delay: i * 0.08 },
});

/* ═══════════════════════════════════════════════════════════════════════ */
/*                         MAIN COMPONENT                                */
/* ═══════════════════════════════════════════════════════════════════════ */

function ParentPortal() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [enrollments, setEnrollments] = useState<
    {
      id: string;
      status: string;
      child_name: string;
      courses: { name: string; symbol: string | null } | null;
    }[]
  >([]);

  /* ──── Data fetching (preserved exactly from original) ──── */

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [pc, en] = await Promise.all([
          supabase.from("parent_children").select("student_id").eq("parent_id", user.id),
          supabase
            .from("enrollments")
            .select("id,status,child_name,courses(name,symbol)")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false }),
        ]);
        setEnrollments((en.data ?? []) as unknown as typeof enrollments);

        const studentIds = (pc.data ?? []).map((r) => r.student_id as string);
        if (studentIds.length === 0) {
          setLoading(false);
          return;
        }

        const [prData, gmData, lpData, qaData, atData, sbData, ntData, subData] = await Promise.all(
          [
            supabase.from("profiles").select("id,full_name,points,level").in("id", studentIds),
            supabase
              .from("group_members")
              .select("student_id,groups(course_id)")
              .in("student_id", studentIds),
            supabase
              .from("lesson_progress")
              .select("student_id,lesson_id")
              .in("student_id", studentIds),
            supabase
              .from("quiz_attempts")
              .select("student_id,percentage,created_at")
              .in("student_id", studentIds)
              .order("created_at", { ascending: false }),
            supabase
              .from("attendance")
              .select("student_id,status,session_date")
              .in("student_id", studentIds)
              .order("session_date", { ascending: false }),
            supabase.from("student_badges").select("student_id,id").in("student_id", studentIds),
            supabase
              .from("instructor_notes")
              .select("student_id,content,created_at")
              .in("student_id", studentIds)
              .eq("is_private", false)
              .order("created_at", { ascending: false }),
            supabase
              .from("assignment_submissions")
              .select("student_id,status,grade,assignments(max_grade)")
              .in("student_id", studentIds),
          ],
        );

        const allCourseIds = new Set<string>();
        const allGroupIds = new Set<string>();
        (gmData.data ?? []).forEach((r) => {
          const cid = (r.groups as any)?.course_id;
          if (cid) allCourseIds.add(cid);
          if (r.groups) allGroupIds.add((r as any).group_id);
        });

        const [{ data: lsData }, { data: asmData }] = await Promise.all([
          supabase
            .from("lessons")
            .select("id,course_id")
            .in("course_id", allCourseIds.size ? Array.from(allCourseIds) : ["__none__"]),
          supabase
            .from("assignments")
            .select("id,group_id")
            .in("group_id", allGroupIds.size ? Array.from(allGroupIds) : ["__none__"]),
        ]);

        const childData: Child[] = studentIds.map((sid) => {
          const pr = (prData.data ?? []).find((p) => p.id === sid);
          const gm = (gmData.data ?? []).filter((g) => g.student_id === sid);
          const courseIds = gm.map((g) => (g.groups as any)?.course_id).filter(Boolean);
          const totalLessons = (lsData ?? []).filter((l) => courseIds.includes(l.course_id)).length;

          const attempts = (qaData.data ?? [])
            .filter((q) => q.student_id === sid)
            .map((a) => a.percentage as number);
          const present = (atData.data ?? []).filter(
            (a) => a.student_id === sid && (a.status === "present" || a.status === "late"),
          ).length;

          const myGroupIds = gm.map((g) => (g as any).group_id).filter(Boolean);
          const studentAsm = (asmData ?? []).filter((a) => myGroupIds.includes(a.group_id));
          const studentSub = (subData.data ?? []).filter((s) => s.student_id === sid);

          const submittedCount = studentSub.length;
          const pendingCount = Math.max(0, studentAsm.length - submittedCount);
          const gradedSubs = studentSub.filter((s) => s.status === "graded" && s.grade != null);
          const asmAvg = gradedSubs.length
            ? Math.round(
                gradedSubs.reduce(
                  (a, b) =>
                    a + ((b.grade as number) / ((b.assignments as any)?.max_grade || 100)) * 100,
                  0,
                ) / gradedSubs.length,
              )
            : 0;

          const recentAbsences = (atData.data ?? [])
            .filter((a) => a.student_id === sid && ["absent", "late"].includes(a.status))
            .filter(
              (a) => new Date(a.session_date).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000,
            )
            .slice(0, 5) as { session_date: string; status: string }[];

          return {
            student_id: sid,
            profile: pr
              ? {
                  full_name: pr.full_name,
                  points: pr.points,
                  level: pr.level,
                }
              : null,
            lessons_done: (lpData.data ?? []).filter((l) => l.student_id === sid).length,
            lessons_total: totalLessons,
            quizzes_taken: attempts.length,
            avg_score: attempts.length
              ? Math.round(attempts.reduce((a, b) => a + b, 0) / attempts.length)
              : 0,
            attendance_present: present,
            attendance_total: (atData.data ?? []).filter((a) => a.student_id === sid).length,
            badges: (sbData.data ?? []).filter((b) => b.student_id === sid).length,
            notes: (ntData.data ?? []).filter((n) => n.student_id === sid).slice(0, 3) as {
              content: string;
              created_at: string;
            }[],
            assignments_pending: pendingCount,
            assignments_submitted: submittedCount,
            assignments_avg_grade: asmAvg,
            absence_alerts: recentAbsences,
            recent_quizzes: (qaData.data ?? []).filter((q) => q.student_id === sid).slice(0, 5) as {
              percentage: number;
              created_at: string;
            }[],
          };
        });

        setChildren(childData);
        if (childData.length > 0) {
          setSelectedChild(childData[0].student_id);
        }
      } catch (err) {
        console.error("Error fetching parent portal data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  /* ──── Derived data for selected child ──── */

  const child = children.find((c) => c.student_id === selectedChild) ?? children[0] ?? null;

  const progress = child
    ? child.lessons_total
      ? Math.round((child.lessons_done / child.lessons_total) * 100)
      : 0
    : 0;
  const attRate = child
    ? child.attendance_total
      ? Math.round((child.attendance_present / child.attendance_total) * 100)
      : 0
    : 0;

  /* Count enrolled courses for this child */
  const childCourseCount = enrollments.filter(
    (e) => child && (e.child_name === child.profile?.full_name || true),
  ).length;

  /* Chart data: quiz performance bar chart */
  const quizChartData = child
    ? child.recent_quizzes
        .slice()
        .reverse()
        .map((q) => ({
          date: new Date(q.created_at).toLocaleDateString("ar-EG", {
            month: "short",
            day: "numeric",
          }),
          score: q.percentage,
        }))
    : [];

  /* Pie chart data: skill distribution */
  const skillData = child
    ? [
        { name: "الكويزات", value: child.avg_score || 1 },
        { name: "الحضور", value: attRate || 1 },
        { name: "الواجبات", value: child.assignments_avg_grade || 1 },
        { name: "الدروس", value: progress || 1 },
      ]
    : [];

  /* ──── Render ──── */

  if (loading) {
    return (
      <RoleGuard allowedRoles={["parent", "admin"]}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RoleGuard>
    );
  }

  return (
    <RoleGuard allowedRoles={["parent", "admin"]}>
      <div className="container mx-auto px-4 py-10 max-w-7xl">
        {/* ─── Header ─── */}
        <motion.div {...fadeUp} className="mb-8">
          <h1 className="text-3xl font-extrabold mb-1">لوحة ولي الأمر 👨‍👩‍👧</h1>
          <p className="text-muted-foreground">تابع تقدم أولادك خطوة بخطوة</p>
        </motion.div>

        {/* ─── No children warning ─── */}
        {children.length === 0 ? (
          <motion.div {...fadeUp}>
            <Card className="rounded-3xl border-dashed bg-amber-500/5 mb-8">
              <CardContent className="p-6 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold mb-1">لم يتم ربط أي طفل بحسابك بعد</div>
                  <p className="text-sm text-muted-foreground">
                    كلم الإدارة لربط حساب طفلك بحسابك حتى تستطيع متابعة تقدمه.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <>
            {/* ═══════ 1. CHILD SELECTOR ═══════ */}
            <motion.div {...fadeUp} className="flex gap-3 overflow-x-auto pb-2 mb-8 scrollbar-none">
              {children.map((c, i) => {
                const isActive = c.student_id === selectedChild;
                const initials = (c.profile?.full_name ?? "?")
                  .split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2);
                return (
                  <motion.button
                    key={c.student_id}
                    {...stagger(i)}
                    onClick={() => setSelectedChild(c.student_id)}
                    className={`
                      flex items-center gap-3 px-5 py-3 rounded-2xl border-2 transition-all
                      shrink-0 cursor-pointer
                      ${
                        isActive
                          ? "border-primary bg-primary/10 shadow-lg shadow-primary/10"
                          : "border-transparent bg-muted/40 hover:bg-muted/70"
                      }
                    `}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback
                        className={
                          isActive
                            ? "bg-primary text-primary-foreground font-bold"
                            : "bg-muted font-bold"
                        }
                      >
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-start">
                      <p className="font-bold text-sm leading-tight">
                        {c.profile?.full_name || "Student"}
                      </p>
                      <p className="text-xs text-muted-foreground">Level {c.profile?.level ?? 1}</p>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>

            {child && (
              <>
                {/* ═══════ 2. OVERVIEW STAT CARDS ═══════ */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard
                    index={0}
                    icon={<BookOpen className="h-5 w-5" />}
                    label="التقدم في الدروس"
                    value={`${progress}%`}
                    sub={`${child.lessons_done}/${child.lessons_total}`}
                    gradient="from-orange-500 to-amber-400"
                  />
                  <StatCard
                    index={1}
                    icon={<Flame className="h-5 w-5" />}
                    label="الحضور"
                    value={`${child.attendance_present}`}
                    sub={`من ${child.attendance_total} حصة`}
                    gradient="from-blue-500 to-cyan-400"
                  />
                  <StatCard
                    index={2}
                    icon={<Zap className="h-5 w-5" />}
                    label="النقاط (XP)"
                    value={`${child.profile?.points ?? 0}`}
                    sub={`${child.badges} شارة`}
                    gradient="from-emerald-500 to-green-400"
                  />
                  <StatCard
                    index={3}
                    icon={<GraduationCap className="h-5 w-5" />}
                    label="الكورسات"
                    value={`${childCourseCount}`}
                    sub="مسجّل"
                    gradient="from-purple-500 to-fuchsia-400"
                  />
                </div>

                {/* ═══════ 3. CHARTS ROW ═══════ */}
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                  {/* Weekly Activity / Quiz Performance Bar Chart */}
                  <motion.div {...stagger(0)}>
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-primary" />
                          أداء الكويزات
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {quizChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={quizChartData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="hsl(var(--border))"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                                axisLine={false}
                                tickLine={false}
                                width={32}
                              />
                              <RechartsTooltip
                                contentStyle={chartTooltipStyle}
                                cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
                              />
                              <Bar
                                dataKey="score"
                                name="النتيجة %"
                                fill="hsl(var(--primary))"
                                radius={[8, 8, 0, 0]}
                                maxBarSize={40}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">
                            لم يقم بحل أي كويزات بعد
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Subject Performance Progress Bars */}
                  <motion.div {...stagger(1)}>
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Star className="h-4 w-4 text-amber-500" />
                          الأداء العام
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5 pt-2">
                        <PerformanceBar
                          label="الكويزات"
                          value={child.avg_score}
                          color="bg-orange-500"
                          hint={`${child.quizzes_taken} كويز`}
                        />
                        <PerformanceBar
                          label="الحضور"
                          value={attRate}
                          color="bg-blue-500"
                          hint={`${child.attendance_present}/${child.attendance_total}`}
                        />
                        <PerformanceBar
                          label="الواجبات"
                          value={child.assignments_avg_grade}
                          color="bg-emerald-500"
                          hint={`${child.assignments_submitted} مُسلم, ${child.assignments_pending} معلق`}
                        />
                        <PerformanceBar
                          label="الدروس"
                          value={progress}
                          color="bg-purple-500"
                          hint={`${child.lessons_done}/${child.lessons_total}`}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* ═══════ 4. SKILL DISTRIBUTION & RECENT ACHIEVEMENTS ═══════ */}
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                  {/* Skill Distribution Pie Chart */}
                  <motion.div {...stagger(2)}>
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-purple-500" />
                          توزيع المهارات
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-center gap-6 flex-wrap">
                          <ResponsiveContainer width={180} height={180}>
                            <PieChart>
                              <Pie
                                data={skillData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={4}
                                dataKey="value"
                                strokeWidth={0}
                              >
                                {skillData.map((_, idx) => (
                                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip contentStyle={chartTooltipStyle} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-2">
                            {skillData.map((s, idx) => (
                              <div key={s.name} className="flex items-center gap-2 text-sm">
                                <span
                                  className="inline-block h-3 w-3 rounded-full"
                                  style={{
                                    backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                                  }}
                                />
                                <span className="text-muted-foreground">{s.name}</span>
                                <span className="font-bold">{s.value}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Recent Achievements / Absence Alerts */}
                  <motion.div {...stagger(3)}>
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-amber-500" />
                          آخر الكويزات والحضور
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* Recent quizzes */}
                        {child.recent_quizzes.length > 0 ? (
                          child.recent_quizzes.map((q, i) => (
                            <div
                              key={i}
                              className="flex justify-between items-center text-sm p-3 rounded-xl bg-muted/30"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                    q.percentage >= 80
                                      ? "bg-emerald-500"
                                      : q.percentage >= 50
                                        ? "bg-amber-500"
                                        : "bg-rose-500"
                                  }`}
                                >
                                  {q.percentage}
                                </div>
                                <span className="text-muted-foreground">
                                  كويز – {new Date(q.created_at).toLocaleDateString("ar-EG")}
                                </span>
                              </div>
                              <Badge variant={q.percentage >= 80 ? "default" : "secondary"}>
                                {q.percentage >= 80
                                  ? "ممتاز"
                                  : q.percentage >= 50
                                    ? "جيد"
                                    : "يحتاج تحسين"}
                              </Badge>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            لم يقم بحل أي كويزات بعد
                          </p>
                        )}

                        {/* Absence alerts */}
                        {child.absence_alerts.length > 0 && (
                          <div className="border-t pt-3 mt-2">
                            <p className="text-xs font-bold text-muted-foreground mb-2 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3 text-amber-500" />
                              تنبيهات الغياب (آخر 30 يوم)
                            </p>
                            {child.absence_alerts.map((a, i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center text-sm p-2 rounded-lg bg-muted/20 mb-1"
                              >
                                <span>{new Date(a.session_date).toLocaleDateString("ar-EG")}</span>
                                <Badge
                                  variant={a.status === "absent" ? "destructive" : "secondary"}
                                  className={
                                    a.status === "late" ? "bg-amber-500 hover:bg-amber-600" : ""
                                  }
                                >
                                  {a.status === "absent" ? "غياب" : "تأخير"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}

                        {child.absence_alerts.length === 0 && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 pt-2">
                            <CheckCircle className="h-3 w-3 text-emerald-500" />
                            لا توجد غيابات مؤخراً
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* ═══════ 5. INSTRUCTOR NOTES & QUICK ACTIONS ═══════ */}
                <div className="grid lg:grid-cols-3 gap-6 mb-10">
                  {/* Instructor Notes / Upcoming Activity Feed (2 cols) */}
                  <motion.div {...stagger(4)} className="lg:col-span-2">
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-primary" />
                          ملاحظات المدرس
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {child.notes.length > 0 ? (
                          <div className="space-y-3">
                            {child.notes.map((n, i) => (
                              <motion.div
                                key={i}
                                {...stagger(i)}
                                className="p-4 rounded-2xl bg-muted/30 border border-border/50"
                              >
                                <p className="text-sm leading-relaxed">{n.content}</p>
                                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {new Date(n.created_at).toLocaleDateString("ar-EG")}
                                </p>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                            <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
                            <p className="text-sm">لا توجد ملاحظات حالياً</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Quick Actions (1 col) */}
                  <motion.div {...stagger(5)}>
                    <Card className="rounded-3xl border-0 shadow-soft h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Zap className="h-4 w-4 text-amber-500" />
                          إجراءات سريعة
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button
                          asChild
                          className="w-full rounded-xl justify-between bg-gradient-to-r from-primary to-primary/80 text-primary-foreground border-0 h-12"
                        >
                          <Link to="/courses">
                            <span className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4" />
                              استعرض الكورسات
                            </span>
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          className="w-full rounded-xl justify-between h-12"
                        >
                          <Link to="/contact">
                            <span className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              تواصل معنا
                            </span>
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </>
            )}
          </>
        )}

        {/* ═══════ ENROLLMENTS SECTION ═══════ */}
        <motion.div {...fadeUp}>
          <h2 className="text-2xl font-bold mb-4">طلبات التسجيل</h2>
          {enrollments.length === 0 ? (
            <Card className="rounded-3xl border-dashed">
              <CardContent className="p-10 text-center">
                <p className="text-muted-foreground mb-4">لسه مسجلتش في أي كورس</p>
                <Button
                  asChild
                  className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                >
                  <Link to="/courses">Browse Courses</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrollments.map((e, i) => (
                <motion.div key={e.id} {...stagger(i)}>
                  <Card className="rounded-2xl border-0 shadow-soft hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="text-3xl mb-2">{e.courses?.symbol || "📚"}</div>
                      <h3 className="font-bold">{e.courses?.name}</h3>
                      <p className="text-sm text-muted-foreground">{e.child_name}</p>
                      <Badge
                        className="mt-2"
                        variant={e.status === "confirmed" ? "default" : "secondary"}
                      >
                        {e.status}
                      </Badge>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </RoleGuard>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*                         SUB-COMPONENTS                                */
/* ═══════════════════════════════════════════════════════════════════════ */

/* ─── Gradient Stat Card ─── */

function StatCard({
  icon,
  label,
  value,
  sub,
  gradient,
  index,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  gradient: string;
  index: number;
}) {
  return (
    <motion.div {...stagger(index)}>
      <Card
        className={`
          rounded-2xl border-0 overflow-hidden relative
          bg-gradient-to-br ${gradient} text-white shadow-lg
        `}
      >
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              {icon}
            </div>
          </div>
          <p className="text-2xl font-extrabold leading-none mb-1">{value}</p>
          <p className="text-xs opacity-80">{label}</p>
          <p className="text-[11px] opacity-60 mt-1">{sub}</p>
          {/* Decorative circle */}
          <div className="absolute -bottom-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ─── Performance Progress Bar ─── */

function PerformanceBar({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: number;
  color: string;
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm font-bold">{value}%</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-muted/50 overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(value, 100)}%` }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
